import config from '../../config';
import { getToken } from '../lib/localStorageHandler';

export interface MultiFileAskAIRequest {
  fileIds: string[];
  question: string;
  sessionId?: string | null;
}

export interface GroqToolCallRequest {
  fileIds?: string[];
  question: string;
  sessionId?: string;
  enabledTools?: string[];
  model?: string;
}

export interface ToolCallEvent {
  type: 'tool_call';
  toolName: string;
  toolId: string;
  args: Record<string, unknown>;
  status: 'started' | 'completed';
  result?: unknown;
}

export interface SummarizedMultiFileAskAIRequest {
  fileIds?: string[]; // Optional - will auto-search if not provided
  question: string;
  sessionId?: string;
  modelName?: string;
  searchLimit?: number; // Number of files to auto-select when searching (default: 5)
}

export interface MultiFileAskAIResponse {
  success: boolean;
  data?: {
    sessionId: string;
    answer: {
      role: string;
      content: string;
      partial: boolean;
      refusal: string;
    };
    fileCount: number;
  };
  message?: string;
}

export interface DeleteSessionResponse {
  success: boolean;
  message?: string;
}

export interface SessionListItem {
  _id: string;
  fileIds: string[];
  conversation: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp?: string;
    screenshots?: Array<{
      url: string;
      fileName?: string;
      timestamp?: string;
      fileId?: string;
    }>;
  }>;
  createdAt: string;
  updatedAt: string;
  teamId?: string;
}

export interface GetSessionsResponse {
  success: boolean;
  data?: {
    sessions: SessionListItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  message?: string;
}

/**
 * Ask a question across multiple files using AI
 * @param teamId - The team ID
 * @param request - Request payload containing fileIds, question, and optional sessionId
 * @returns Response with AI answer and session information
 */
export const askMultiFileQuestion = async (
  teamId: string,
  request: MultiFileAskAIRequest,
): Promise<MultiFileAskAIResponse> => {
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file/${teamId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to analyze recordings');
  }

  return response.json();
};

/**
 * Get multi-file Ask AI sessions history
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @param teamId - Optional team ID filter
 * @returns Response with sessions list and pagination
 */
export const getMultiFileSessions = async (
  page: number = 1,
  limit: number = 20,
  teamId?: string,
): Promise<GetSessionsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (teamId) {
    params.append('teamId', teamId);
  }

  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file/sessions?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch sessions');
  }

  return response.json();
};

/**
 * Get Groq chat sessions history (from ToolChatSession collection)
 * This is used by the toolcall-groq chat feature
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 20)
 * @param teamId - Optional team ID filter
 * @returns Response with sessions list and pagination
 */
export const getGroqSessions = async (
  page: number = 1,
  limit: number = 20,
  teamId?: string,
): Promise<GetSessionsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (teamId) {
    params.append('teamId', teamId);
  }

  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/toolcall-groq/sessions?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch Groq sessions');
  }

  return response.json();
};

/**
 * Delete a multi-file Ask AI session
 * @param sessionId - The session ID to delete
 * @returns Response indicating success or failure
 */
export const deleteMultiFileSession = async (
  sessionId: string,
): Promise<DeleteSessionResponse> => {
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file/session/${sessionId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to delete session');
  }

  return response.json();
};

/**
 * Ask a question about all recordings in a folder
 * @param teamId - The team ID
 * @param folderId - The folder ID
 * @param question - The question to ask
 * @param sessionId - Optional session ID for conversation continuity
 * @returns Response with AI answer and session information
 */
export const askFolderQuestion = async (
  teamId: string,
  folderId: string,
  question: string,
  sessionId?: string | null,
): Promise<MultiFileAskAIResponse> => {
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/folder/${teamId}/${folderId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        question,
        sessionId,
      }),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to analyze folder recordings');
  }

  return response.json();
};

/**
 * Ask a question across multiple files using AI with summarization
 * This endpoint uses a more efficient summarization approach
 * @param teamId - The team ID
 * @param request - Request payload containing fileIds, question, and optional model settings
 * @returns Response with AI answer
 */
export const askMultiFileSummarizedQuestion = async (
  teamId: string,
  request: SummarizedMultiFileAskAIRequest,
): Promise<MultiFileAskAIResponse> => {
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file-summarized/${teamId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(request),
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to analyze recordings');
  }

  return response.json();
};

/**
 * Ask a question about a single file using AI with real-time streaming
 * @param teamId - The team ID
 * @param fileId - The file ID
 * @param question - The question to ask
 * @param sessionId - Optional session ID for conversation continuity
 * @param onChunk - Callback function called for each text chunk received
 * @param options - Optional configuration for streaming behavior (includes mediaAnalysisOptions)
 * @returns Promise with sessionId after stream completes
 */
export const askSingleFileQuestionStream = async (
  teamId: string,
  fileId: string,
  question: string,
  sessionId: string | undefined | null,
  onChunk: (chunk: string) => void,
  options?: {
    streamDelay?: number;
    signal?: AbortSignal;
    mediaAnalysisOptions?: any;
  },
): Promise<{ sessionId: string }> => {
  const streamDelay = options?.streamDelay ?? 0;

  // If video analysis is requested, use the multiModel endpoint which supports video via Gemini
  if (options?.mediaAnalysisOptions?.video) {
    const response = await fetch(
      `${config.authBaseUrlV2}/files/${fileId}/ask/multimodal`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          promptText: question,
          mediaAnalysisOptions: options.mediaAnalysisOptions,
        }),
        signal: options?.signal,
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to analyze video');
    }

    const responseData = await response.json();
    const { sessionId: newSessionId, answer } = responseData.data;

    // Deliver the full answer via onChunk (multiModel is non-streaming)
    if (answer?.content) {
      onChunk(answer.content);
    }

    return { sessionId: newSessionId };
  }

  // Use the multi-file-stream endpoint with a single file and sessionType: 'single-file'
  const requestBody: any = {
    fileIds: [fileId],
    question,
    sessionType: 'single-file',
  };

  if (sessionId) {
    requestBody.sessionId = sessionId;
  }

  // Add media analysis options if provided
  if (options?.mediaAnalysisOptions) {
    requestBody.mediaAnalysisOptions = options.mediaAnalysisOptions;
  }

  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file-stream/${teamId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(requestBody),
      signal: options?.signal,
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to analyze recording');
  }

  // Get session ID from response headers
  const newSessionId = response.headers.get('X-Session-Id') || '';

  // Parse SSE stream
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body available for streaming');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let displayedText = '';

  // Word-by-word display queue
  const wordQueue: string[] = [];

  // Function to display words from queue one by one
  const displayWords = async () => {
    while (wordQueue.length > 0) {
      const word = wordQueue.shift();
      if (word !== undefined) {
        displayedText += word;
        onChunk(displayedText);

        if (streamDelay > 0 && wordQueue.length > 0) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, streamDelay));
        }
      }
    }
  };

  try {
    let done = false;
    while (!done) {
      // eslint-disable-next-line no-await-in-loop
      const result = await reader.read();
      done = result.done;

      if (done) {
        // Display any remaining words
        // eslint-disable-next-line no-await-in-loop
        await displayWords();
        break;
      }

      // Decode the chunk and add to buffer
      buffer += decoder.decode(result.value, { stream: true });

      // Process complete SSE messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      // Parse chunks and split into words
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.chunk) {
              // Split chunk into words (including spaces and punctuation)
              // This regex splits on word boundaries but keeps spaces
              const words = data.chunk.split(/(\s+)/);

              // Add words to queue
              words.forEach((word: string) => {
                if (word) {
                  wordQueue.push(word);
                }
              });

              // Display queued words
              // eslint-disable-next-line no-await-in-loop
              await displayWords();
            }
          } catch (e) {
            console.error(
              '[Single File Streaming] Failed to parse SSE chunk:',
              e,
              'Line:',
              line,
            );
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  return { sessionId: newSessionId };
};

/**
 * Ask a question across multiple files using AI with real-time streaming
 * @param teamId - The team ID
 * @param request - Request payload containing fileIds, question, and optional model settings
 * @param onChunk - Callback function called for each text chunk received
 * @param options - Optional configuration for streaming behavior
 * @returns Promise with sessionId and selectedFileIds after stream completes
 */
export const askMultiFileQuestionStream = async (
  teamId: string,
  request: SummarizedMultiFileAskAIRequest,
  onChunk: (chunk: string) => void,
  options?: {
    streamDelay?: number;
    signal?: AbortSignal;
    onFilesReceived?: (fileIds: string[]) => void;
  },
): Promise<{ sessionId: string; selectedFileIds?: string[] }> => {
  console.log('[askMultiFileQuestionStream] Function called with options:', {
    hasOptions: !!options,
    hasOnFilesReceived: !!options?.onFilesReceived,
    optionsKeys: options ? Object.keys(options) : [],
  });
  const streamDelay = options?.streamDelay ?? 0; // No delay - display as fast as backend sends
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/multi-file-stream/${teamId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(request),
      signal: options?.signal,
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to analyze recordings');
  }

  // Get session ID from response headers
  const sessionId = response.headers.get('X-Session-Id') || '';

  // Parse SSE stream
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body available for streaming');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let displayedText = '';
  let selectedFileIds: string[] | undefined;

  // Word-by-word display queue
  const wordQueue: string[] = [];

  // Function to display words from queue one by one
  const displayWords = async () => {
    while (wordQueue.length > 0) {
      const word = wordQueue.shift();
      if (word !== undefined) {
        displayedText += word;
        onChunk(displayedText);

        if (streamDelay > 0 && wordQueue.length > 0) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, streamDelay));
        }
      }
    }
  };

  try {
    let done = false;
    while (!done) {
      // eslint-disable-next-line no-await-in-loop
      const result = await reader.read();
      done = result.done;

      if (done) {
        // Display any remaining words
        // eslint-disable-next-line no-await-in-loop
        await displayWords();
        console.log('[Streaming] Stream complete');
        break;
      }

      // Decode the chunk and add to buffer
      buffer += decoder.decode(result.value, { stream: true });

      // Process complete SSE messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      // Parse chunks and split into words
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            // Handle "files" event - backend sends selected fileIds
            if (data.type === 'files' && data.fileIds) {
              console.log(
                '[multiFileAskAIService] Received files event from backend:',
                data.fileIds,
              );
              selectedFileIds = data.fileIds;
              console.log(
                '[multiFileAskAIService] Set selectedFileIds to:',
                selectedFileIds,
              );
              // Immediately notify the caller about selected files
              if (options?.onFilesReceived && selectedFileIds) {
                console.log(
                  '[multiFileAskAIService] onFilesReceived callback exists:',
                  typeof options.onFilesReceived,
                );
                console.log(
                  '[multiFileAskAIService] Calling onFilesReceived callback with:',
                  selectedFileIds,
                );
                try {
                  options.onFilesReceived(selectedFileIds);
                  console.log(
                    '[multiFileAskAIService] onFilesReceived callback completed successfully',
                  );
                } catch (error) {
                  console.error(
                    '[multiFileAskAIService] Error calling onFilesReceived callback:',
                    error,
                  );
                }
              } else {
                console.log(
                  '[multiFileAskAIService] onFilesReceived callback NOT called - callback exists:',
                  !!options?.onFilesReceived,
                  'selectedFileIds exists:',
                  !!selectedFileIds,
                );
              }
            }
            // Handle "chunk" event - AI response text
            else if (data.chunk) {
              // Split chunk into words (including spaces and punctuation)
              // This regex splits on word boundaries but keeps spaces
              const words = data.chunk.split(/(\s+)/);

              // Add words to queue
              words.forEach((word: string) => {
                if (word) {
                  wordQueue.push(word);
                }
              });

              // Display queued words
              // eslint-disable-next-line no-await-in-loop
              await displayWords();
            }
          } catch (e) {
            console.error(
              '[Streaming] Failed to parse SSE chunk:',
              e,
              'Line:',
              line,
            );
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  console.log(
    '[multiFileAskAIService] Returning sessionId and selectedFileIds:',
    { sessionId, selectedFileIds },
  );
  return { sessionId, selectedFileIds };
};

/**
 * Ask a question using Groq with tool calling capabilities
 * The LLM can call tools like search_content, list_recent_files, get_file_summary, get_user_memory
 * @param teamId - The team ID
 * @param request - Request payload containing fileIds, question, sessionId, and enabledTools
 * @param onChunk - Callback function called for each text chunk received
 * @param options - Optional configuration for streaming behavior
 * @returns Promise with sessionId after stream completes
 */
export const askGroqWithToolsStream = async (
  teamId: string,
  request: GroqToolCallRequest,
  onChunk: (chunk: string) => void,
  options?: {
    streamDelay?: number;
    signal?: AbortSignal;
    onToolCallStart?: (toolCall: ToolCallEvent) => void;
    onToolCallComplete?: (toolCall: ToolCallEvent) => void;
    onFilesReceived?: (fileIds: string[]) => void;
  },
): Promise<{ sessionId: string; selectedFileIds?: string[] }> => {
  const streamDelay = options?.streamDelay ?? 0;

  const requestBody: GroqToolCallRequest = {
    fileIds: request.fileIds || [],
    question: request.question,
    enabledTools: request.enabledTools || [
      'search_content',
      'list_recent_files',
      'get_file_summary',
      'get_file_transcript',
      'get_user_memory',
      'rename_file',
      'list_folders',
      'list_folder_contents',
      'get_folder_info',
      'get_video_screenshot',
      'trim_video',
      'combine_videos',
    ],
    model: request.model,
  };

  if (request.sessionId) {
    requestBody.sessionId = request.sessionId;
  }

  // Use the streaming endpoint for real SSE streaming
  const response = await fetch(
    `${config.authBaseUrlV2}/files/ask/toolcall-groq-stream/${teamId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(requestBody),
      signal: options?.signal,
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to process AI request');
  }

  // Check content type to determine response format
  const contentType = response.headers.get('Content-Type') || '';
  const isSSE = contentType.includes('text/event-stream');

  // Get session ID from response headers (for SSE) or from JSON body (for non-SSE)
  let sessionId = response.headers.get('X-Session-Id') || '';
  let selectedFileIds: string[] | undefined;

  if (!isSSE) {
    // Fallback: Handle regular JSON response (shouldn't happen with streaming endpoint)
    const jsonResponse = await response.json();
    console.log('[Groq] Received JSON response (fallback):', jsonResponse);

    if (jsonResponse.success && jsonResponse.data) {
      const { data } = jsonResponse;

      // Extract session ID from response body
      sessionId = data.sessionId || sessionId;

      // Process tool calls if present
      const toolCalls = data.toolsCalled || [];
      const hasToolCalls = Array.isArray(toolCalls) && toolCalls.length > 0;

      if (hasToolCalls) {
        // Process tool calls with staggered timing so UI can display them
        for (let i = 0; i < toolCalls.length; i += 1) {
          const tool = toolCalls[i] as {
            name: string;
            id?: string;
            arguments?: Record<string, unknown>;
            result?: unknown;
          };
          const toolId = tool.id || `tool-${Date.now()}-${i}`;

          // Notify tool call started
          const startEvent: ToolCallEvent = {
            type: 'tool_call',
            toolName: tool.name,
            toolId,
            args: tool.arguments || {},
            status: 'started',
          };
          options?.onToolCallStart?.(startEvent);

          // Small delay between start and complete to show the running state
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(resolve, 300);
          });

          // Notify tool call completed
          const completeEvent: ToolCallEvent = {
            type: 'tool_call',
            toolName: tool.name,
            toolId,
            args: tool.arguments || {},
            status: 'completed',
            result: tool.result,
          };
          options?.onToolCallComplete?.(completeEvent);
        }

        // Additional delay after all tool calls complete before showing response
        await new Promise((resolve) => {
          setTimeout(resolve, 500);
        });
      }

      // Extract and display the answer content
      if (data.answer?.content) {
        onChunk(data.answer.content);
      }

      // Handle file IDs if present
      if (data.fileIds && Array.isArray(data.fileIds)) {
        selectedFileIds = data.fileIds;
        if (selectedFileIds) {
          options?.onFilesReceived?.(selectedFileIds);
        }
      }
    }

    return { sessionId, selectedFileIds };
  }

  // Handle SSE streaming response
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body available for streaming');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let displayedText = '';

  // Word-by-word display queue
  const wordQueue: string[] = [];

  // Track if we just completed a tool call - need to add paragraph break before next chunk
  let justCompletedToolCall = false;

  // Function to display words from queue one by one
  const displayWords = async () => {
    while (wordQueue.length > 0) {
      const word = wordQueue.shift();
      if (word !== undefined) {
        displayedText += word;
        onChunk(displayedText);

        if (streamDelay > 0 && wordQueue.length > 0) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, streamDelay));
        }
      }
    }
  };

  try {
    let done = false;
    while (!done) {
      // eslint-disable-next-line no-await-in-loop
      const result = await reader.read();
      done = result.done;

      if (done) {
        // Display any remaining words
        // eslint-disable-next-line no-await-in-loop
        await displayWords();
        break;
      }

      // Decode the chunk and add to buffer
      buffer += decoder.decode(result.value, { stream: true });

      // Process complete SSE messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      // Parse chunks and handle different event types
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));

            // Handle "files" event - backend sends selected fileIds
            if (data.type === 'files' && data.fileIds) {
              selectedFileIds = data.fileIds;
              if (options?.onFilesReceived && selectedFileIds) {
                options.onFilesReceived(selectedFileIds);
              }
            }
            // Handle "tool_call" event - LLM is calling a tool
            else if (data.type === 'tool_call') {
              const toolCallEvent: ToolCallEvent = {
                type: 'tool_call',
                toolName: data.toolName || data.tool_name,
                toolId: data.toolId || data.tool_id || '',
                args: data.args || data.arguments || {},
                status: data.status || 'started',
                result: data.result,
              };

              if (toolCallEvent.status === 'started') {
                options?.onToolCallStart?.(toolCallEvent);
              } else if (toolCallEvent.status === 'completed') {
                options?.onToolCallComplete?.(toolCallEvent);
                // Mark that we completed a tool call - next chunk should have paragraph break
                justCompletedToolCall = true;
              }
            }
            // Handle "chunk" event - AI response text
            else if (data.chunk) {
              // If we just completed a tool call, add paragraph break before continuing
              if (justCompletedToolCall && displayedText.length > 0) {
                wordQueue.push('\n\n');
                justCompletedToolCall = false;
              }

              // Normalize chunk: trim leading newline if this is the start of text
              // (fixes issue where model generates "I\nsearched" as first tokens)
              let chunkText = data.chunk;
              if (displayedText.length === 0) {
                chunkText = chunkText.replace(/^\n+/, '');
              }

              // Split chunk into words (including spaces and punctuation)
              const words = chunkText.split(/(\s+)/);

              // Add words to queue
              words.forEach((word: string) => {
                if (word) {
                  wordQueue.push(word);
                }
              });

              // Display queued words
              // eslint-disable-next-line no-await-in-loop
              await displayWords();
            }
            // Handle "done" event - extract sessionId from body (more reliable than header)
            else if (data.type === 'done' && data.sessionId) {
              sessionId = data.sessionId;
            }
            // Handle "error" event - propagate error to caller
            else if (data.type === 'error') {
              const errorMessage =
                data.message ||
                'An error occurred while processing your request';
              console.error(
                '[Groq Streaming] Received error event:',
                errorMessage,
              );
              throw new Error(errorMessage);
            }
          } catch (e) {
            // Re-throw intentional errors, only log parse errors
            if (
              e instanceof Error &&
              !e.message.includes('Unexpected token') &&
              !e.message.includes('JSON')
            ) {
              throw e;
            }
            console.error(
              '[Groq Streaming] Failed to parse SSE chunk:',
              e,
              'Line:',
              line,
            );
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  return { sessionId, selectedFileIds };
};
